import SelectField from '@pelckmans/elementis/components/select-field';
import { useTranslation } from 'react-i18next';
import { useResultsDataContext } from '../../context/ResultsDataContext';
import { useSchools } from '../../hooks/useSchools';
import { useFilters } from '../../context/filter-context/FilterContext';
import ModuleSelectionField from '../../../../../shared-components/ModuleSelectionField';
import { groupBelongsToSelectedSchoolYear } from '../../utils/groupBelongsToSelectedSchoolYear';

interface Props {
  filtersDisabled: boolean;
}

export default function StudentFilter({ filtersDisabled }: Props) {
  const { t } = useTranslation();

  const {
    selectedSchoolYear,
    selectedSchool,
    selectedGroups,
    selectedModule,
    setSelectedModule,
    setSelectedSchoolYear,
    setSelectedSchool,
    setSelectedGroups,
  } = useFilters();
  const { schoolYears, groups, modules } = useResultsDataContext();
  const schools = useSchools(selectedSchoolYear);

  return (
    <>
      <ModuleSelectionField
        key="module-filter"
        modules={modules}
        selectedModule={selectedModule}
        setSelectedModule={setSelectedModule}
        ariaLabel={t('PAGES.RESULTS.PER_STUDENT.FILTER.MODULE.A11Y_LABEL')}
        dropdownMenuAriaLabel={t('PAGES.RESULTS.PER_STUDENT.FILTER.MODULE.DROPDOWN_MENU.A11Y_LABEL')}
      />
      <SelectField
        value={selectedSchoolYear}
        onChange={setSelectedSchoolYear}
        required
        size="sm"
        aria-label={t('PAGES.RESULTS.PER_STUDENT.FILTER.SCHOOL_YEAR.A11Y_LABEL')}
        renderValueContent={year => year.title}
        dropdownMenuProps={{
          ariaLabel: t('PAGES.RESULTS.PER_STUDENT.FILTER.SCHOOL_YEAR.DROPDOWN_MENU.A11Y_LABEL'),
        }}
        disabled={filtersDisabled}
        id="id"
      >
        {[schoolYears!.current, schoolYears!.previous].map(year => (
          <SelectField.Item key={year.id} text={year.title} value={year} />
        ))}
      </SelectField>
      <SelectField
        value={selectedSchool}
        onChange={setSelectedSchool}
        required
        size="sm"
        aria-label={t('PAGES.RESULTS.PER_STUDENT.FILTER.SCHOOL.A11Y_LABEL')}
        renderValueContent={school => school.name}
        dropdownMenuProps={{
          ariaLabel: t('PAGES.RESULTS.PER_STUDENT.FILTER.SCHOOL.DROPDOWN_MENU.A11Y_LABEL'),
        }}
        disabled={filtersDisabled}
        matchOn="id"
      >
        {schools?.map(school => (
          <SelectField.Item key={school.id} text={school.name} value={school} />
        ))}
      </SelectField>
      <SelectField
        value={selectedGroups[0] || null}
        onChange={g => setSelectedGroups([g])}
        required
        size="sm"
        aria-label={t('PAGES.RESULTS.PER_STUDENT.FILTER.GROUP.A11Y_LABEL')}
        renderValueContent={group => group.name}
        dropdownMenuProps={{
          ariaLabel: t('PAGES.RESULTS.PER_STUDENT.FILTER.GROUP.DROPDOWN_MENU.A11Y_LABEL'),
        }}
        disabled={filtersDisabled}
        matchOn="id"
      >
        {groups
          .filter(
            g =>
              g.school.id === selectedSchool?.id &&
              groupBelongsToSelectedSchoolYear(g, selectedSchoolYear?.id) &&
              g.acceptedMemberCount > 0,
          )
          .map(group => (
            <SelectField.Item key={group.id} text={group.name} value={group} />
          ))}
      </SelectField>
    </>
  );
}
